import * as React from "react";
import {inject, observer} from "mobx-react";

import cn from "classnames";

import {ClipsFullscreenStore} from "../../lib/ClipsFullscreenStore";
import {
    AnimationType,
    CLIP_URLS,
    Direction,
    DivExtendedByInViewportFunction,
    FullScreenItem,
    FullscreenItemType
} from "../../lib/types";

import {ClipsFullscreenController} from "../../lib/ClipsFullscreenController";

import classes from "./ClipsFullscreen.styl";
import {Clip} from "../ui/Clip/Clip";
import {
    FADE_IN_TIMING, INTERSECTION_OBSERVER_THRESHOLDS,
    PREVENT_SCROLL_OFFSET,
    SCROLL_ANIMATION_TIMING
} from "../../config";

import {useGlobalModule} from "@ok/web/hooks/useGlobalModule";

import {ClipNotAllowed} from "../ui/ClipNotAllowed/ClipNotAllowed";
import {ClipSkeleton} from "../ui/ClipSkeleton/ClipSkeleton";
import {getToast} from "../ui/Toast/Toast";
import {Fade} from "../ui/Fade/Fade";
import {
    CommonFullscreenHeader
} from "../ui/common/CommonFullscreenHeader/CommonFullscreenHeader";
import {isFunction} from "OK/modules/shims/typeDetection";
import { ClipNotAllowedWrapper } from "./ItemsWrappers/ClipNotAllowedWrapper/ClipNotAllowedWrapper";
import {
    CommonFullscreenItemWrapper
} from "./ItemsWrappers/CommonFullscreenItemWrapper/CommonFullscreenItemWrapper";


export interface IClipsFullscreenProps {
    store?: ClipsFullscreenStore;
    ctrl?: ClipsFullscreenController;

    open?: boolean;
    onClose?: () => void;
}


export const ClipsFullscreen: React.FC<IClipsFullscreenProps> = inject("ctrl")(observer(
    (props: IClipsFullscreenProps) => {
        const ctrl = props.ctrl!;
        const store = ctrl.store!;

        const bottomMenu = useGlobalModule(() => window.OK.bottomMenu).current;

        const handleResize = React.useCallback(() => {
            store.setCurrentWindowInnerHeight();
            store.checkOffset();
        }, [store]);

        const handleHistoryBack = React.useCallback(() => {
            if (store.isFullscreenOpen && store.needToHandleSystemBack && store.isHandleSystemBackFeatureEnabled && !store.isDisableSystemBackHandling) {
                store.setIsFullscreenClosedViaSystemBack(true);
                ctrl.handleClose();
            }
        }, [ctrl, store]);


        React.useEffect(() => {
            window.addEventListener("resize", handleResize);

            if (window.OK.navigation.statePopping) {
                window.OK.navigation.statePopping.attach(handleHistoryBack);
            }

            return () => {
                window.removeEventListener("resize", handleResize);
                window.OK.navigation.statePopping.detach(handleHistoryBack);
            };
        }, [handleResize, handleHistoryBack]);

        React.useEffect(() => {
            window.OK.navigationInterop.navigated.attach(() => {
                const state = window.pageCtx && window.pageCtx.stateId;
                store.setIsDisableSystemBackHandling(store.linkHelper.isCurrentStateInStatesForDisableSystemBackHandling());
                store.setOpenState(state === "clipsFullscreen");
            });
        }, [store]);

        React.useEffect(() => {
            if (!store.isFullscreenOpen) {
                return;
            }

            store.isInitialThemeDark = document.documentElement.classList.contains("dark-theme");
            store.setIsVisible(true);
            store.setAnimationType(AnimationType.FADE_IN);

            if (bottomMenu) {
                bottomMenu.close();
            }

            if (store.isFullscreenOpen && !store.isInitialThemeDark && !store.isAnonym) {
                setTimeout(() => {
                    document.documentElement.classList.add("dark-theme");
                }, FADE_IN_TIMING);
            }
        }, [store.isFullscreenOpen, store, bottomMenu]);

        const fullscreenCn = cn(
            classes["clips-fullscreen"],
            {
                "__hidden": !store.isFullscreenOpen,
                [classes["__visible"]]: store.isVisible,
                [classes["__fade-in"]]: store.animationType === AnimationType.FADE_IN,
                [classes["__fade-out"]]: store.animationType === AnimationType.FADE_OUT,
                [classes["__instant"]]: store.animationType === AnimationType.INSTANT,
            }
        );


        return store.isFullscreenOpen ? (
            <>
                <div className={fullscreenCn}>
                    <Fade>
                        <FullscreenList/>
                    </Fade>
                </div>

                {store.toastShow && getToast(store.toastType, store)}
            </>
        ) : undefined;
    })
);


type TouchMeta = {
    startY: number,
    prevY: number,
    endY: number,

    startX: number,
    prevX: number,
    endX: number,
}

interface IFullscreenList {
    store?: ClipsFullscreenStore;
}
const FullscreenList: React.FC<IFullscreenList> = inject("store")(observer((props) => {
    const store = props.store!;

    const touchMetaRef = React.useRef<TouchMeta>({
        startY: 0,
        prevY: 0,
        endY: 0,

        startX: 0,
        prevX: 0,
        endX: 0,
    });

    const rootRef = React.useRef<HTMLDivElement>(null);
    const loadMoreTriggerRef = React.useRef<HTMLLIElement>(null);

    const _startHandler = React.useCallback((event: TouchEvent | PointerEvent) => {
        handleTouchStart(event, touchMetaRef);
    }, []);

    const _endHandler = React.useCallback((event: TouchEvent | PointerEvent) => {
        handleTouchEnd(event, touchMetaRef, store);
    }, [store]);

    React.useEffect(() => {
        const ref = rootRef.current;

        if (!ref) {
            return;
        }


        if (store.isDesktop) {
            ref.addEventListener("pointerdown", _startHandler);
            ref.addEventListener("pointerup", _endHandler);
        } else {
            ref.addEventListener("touchstart", _startHandler);
            ref.addEventListener("touchend", _endHandler);
        }

        return () => {
            if (!ref) {
                return;
            }

            if (store.isDesktop) {
                ref.removeEventListener("pointerdown", _startHandler);
                ref.removeEventListener("pointerup", _endHandler);
            } else {
                ref.removeEventListener("touchstart", _startHandler);
                ref.removeEventListener("touchend", _endHandler);
            }
        };

    }, [store.isDesktop, _startHandler, _endHandler]);

    React.useEffect(() => {
        if (store.hasMore) {
            const intersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        store.doRequest(CLIP_URLS.GET);
                    }
                }
            );
            intersectionObserver.observe(loadMoreTriggerRef.current!);

            return () => intersectionObserver.disconnect();
        }
    }, [store]);

    React.useEffect(() => {
        const options = {
            root: rootRef.current,
            threshold: INTERSECTION_OBSERVER_THRESHOLDS.COMMON_ITEM,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const target = entry.target as DivExtendedByInViewportFunction;
                if (!target || !isFunction(target.setInViewport)) {
                    return;
                }

                target.setInViewport(entry.isIntersecting);
            });
        }, options);
        store.setUnionObserver(observer);

        return () => {
            if (store.unionObserver) {
                store.unionObserver.disconnect();
            }
        };
    }, [store]);


    const style = {
        transform: `translateY(${store.currentOffset}px)`,
        transition: `transform ${store.isScrollAnimation ? store.scrollAnimationTiming : SCROLL_ANIMATION_TIMING.INSTANT}s`
    };

    const ulCn = cn(
        "clips-fullscreen__list",
        {
            "__isAnimationRunning": store.isAnimationRunning &&
                (!(store.currentItemIndex === 0 && store.scrollDirection === Direction.BACKWARD)),
            "__isForward": store.scrollDirection === Direction.FORWARD,
            "__isDesktop": store.isDesktop,
            "__notAllowedAnimation": store.animateNotAllowedClip,
            "__ios": store.isIOS
        }
    );

    const listRootCn = cn(
        "clips-fullscreen__list-root",
    );

    return (
        <div ref={rootRef} className={listRootCn} data-current={store.currentOffset}>
            <CommonFullscreenHeader/>
            <ul className={ulCn} style={style} >
                {
                    store.currentFullscreenItems.map(renderItem)
                }
                <li ref={loadMoreTriggerRef}></li>
                <ClipSkeleton/>
            </ul>
        </div>
    );
}));


const renderItem = (item: FullScreenItem, index: number) => {
    let component;

    let id;

    switch (item.type) {
        case FullscreenItemType.CLIP:
            if (item.item) {
                id = item.item.meta.movie.movieId;
                component = (
                    <CommonFullscreenItemWrapper bean={item.item} itemType={item.type}>
                        <Clip bean={item.item} isCurrent={item.current} isPrev={item.isPrev} isNext={item.isNext}/>
                    </CommonFullscreenItemWrapper>
                );
            } else {
                component = <ItemStub/>;
            }
            break;

        case FullscreenItemType.STUB:
            component = <ItemStub/>;
            break;

        case FullscreenItemType.NOT_ALLOWED:
            component = (
                <ClipNotAllowedWrapper>
                    <ClipNotAllowed/>
                </ClipNotAllowedWrapper>
            );
            break;

        default:
            component = (
                <ClipNotAllowedWrapper>
                    <ClipNotAllowed/>
                </ClipNotAllowedWrapper>
            );
    }

    const key = id || index;

    return (
        <li key={key}
            data-key={key}
            className={"clips-fullscreen__list-item"}
        >
            {component}
        </li>
    );
};


const ItemStub: React.FC = () => {
    return (
        <div className={classes["clip__stub"]}></div>
    );
};

function handleTouchStart(
    event: PointerEvent | TouchEvent,
    touchMetaRef:React.RefObject<TouchMeta>) {
    if  (touchMetaRef.current ===  null) {
        return;
    }

    if (event instanceof PointerEvent) {
        touchMetaRef.current.startY = event.screenY;
        touchMetaRef.current.startX = event.screenX;
    } else if (event instanceof TouchEvent) {
        touchMetaRef.current.startY = event.changedTouches[0].screenY;
        touchMetaRef.current.startX = event.changedTouches[0].screenX;
    }
}

function handleTouchEnd(
    event: PointerEvent | TouchEvent,
    touchMetaRef: React.RefObject<TouchMeta>,
    store: ClipsFullscreenStore) {

    const isAnimationRunning = store.currentItemIndex === 0 ? false : store.isAnimationRunning;

    if (touchMetaRef.current ===  null || store.isNavigationRunning || isAnimationRunning) {
        return;
    }

    store.clearItemsListBouncingAnimation();

    let current = touchMetaRef.current;

    if (event instanceof PointerEvent) {
        current.endY = event.screenY;
        current.endX = event.screenX;
    } else if (event instanceof TouchEvent) {
        current.endY = event.changedTouches[0].screenY;
        current.endX = event.changedTouches[0].screenX;
    }

    const diffY = current.endY - current.startY;
    const diffX = current.endX - current.startX;

    const absDiffY = Math.abs(diffY);

    if (absDiffY < PREVENT_SCROLL_OFFSET) {
        return;
    }

    if (absDiffY > Math.abs(diffX)) {
        if (diffY > 0) {
            store.setScroll(Direction.BACKWARD);
        } else {
            /* up swipe */
            store.setScroll(Direction.FORWARD);
        }
    }

    store.checkOffset();
    store.setIsAnimationRunning(true);
    store.setOffset();
}
